import useAppSelector from "@/hooks/useAppSelector";
import useFlowboxScript, { type FlowboxScriptProps } from "@/hooks/useFlowboxScript";
import { useEffect, useMemo, useRef } from "react";
import FlowboxSkeleton from "@components/Molecules/FlowboxSkeleton";
import { createCoverOverlay } from "@/utils/coverOverlay";
import { waitForHtmlElements } from "@/utils/waitForElements";

export enum FlowboxType {
  PRODUCT = "product",
  HOME = "home",
  SEARCH_AND_FILTER = "searchAndFilter",
  HOME_CAROUSEL = "homeCarousel",
}
export interface FlowboxProps extends Omit<FlowboxScriptProps, "locale"> {
  disableAutoplay?: boolean;
  type?: `${FlowboxType}`;
  cover?: boolean;
}

// Setting locations based on the store's `store Id`. // This allows Flowbox to display content with correct prices based on the store's location.
const localeConfigFlowbox = {
  13: "es-BR",
  136: "es-PE",
  20: "es-CO",
};

const Flowbox = ({
  flowKey,
  productId,
  tags,
  disableAutoplay = false,
  type = "home",
  enableTimeout = true,
  islazyLoad,
  cover = false,
}: FlowboxProps) => {
  const storedId = useAppSelector((state) => state.cart.storeId);
  const locale = useMemo(() => localeConfigFlowbox[storedId ?? 13], [storedId]);
  const { containerId, isContentReady } = useFlowboxScript({
    flowKey,
    productId,
    tags,
    locale,
    enableTimeout,
    islazyLoad,
  });
  const currentPlayingVideoRef = useRef<HTMLVideoElement | null>(null);

  function playWithHover(div: HTMLDivElement | Element, video: HTMLVideoElement) {
    div.addEventListener("mouseenter", () => {
      if (currentPlayingVideoRef.current && currentPlayingVideoRef.current !== video) {
        currentPlayingVideoRef.current.pause();
        currentPlayingVideoRef.current.currentTime = 0;
      }
      currentPlayingVideoRef.current = video;
      void video.play();
    });

    div.addEventListener("mouseleave", () => {
      video.pause();
      video.currentTime = 0;
    });

    div.addEventListener("touchstart", () => {
      if (currentPlayingVideoRef.current && currentPlayingVideoRef.current !== video) {
        currentPlayingVideoRef.current.pause();
        currentPlayingVideoRef.current.currentTime = 0;
      }
      currentPlayingVideoRef.current = video;
      void video.play();
    });
  }

  const handleVideos = async () => {
    if (!containerId) return;
    const videoContainers = await waitForHtmlElements(containerId, ".bzfy-c-autoplay-video");

    videoContainers.forEach((container) => {
      const video = container.querySelector<HTMLVideoElement>("video");
      if (!video) return;
      video.preload = "none";
      video.pause();
      video.currentTime = 0;

      // Is grid container
      const isGridContainer = container.closest(".bzfy-c-tilegrid-post-container--clickable");

      if (isGridContainer) {
        // logic for grid container
        playWithHover(isGridContainer, video);
      } else {
        // logic carrousel
        playWithHover(container, video);
      }
    });
  };

  const addCover = async () => {
    if (!containerId) return;
    const divs = await waitForHtmlElements(containerId, ".slick-slide");
    divs.forEach((div) => {
      const container = div.querySelector<HTMLDivElement>(".bzfy-c-carousel-post");
      if (container && !container.querySelector(".cover-overlay")) {
        createCoverOverlay(container);
      }
    });
  };

  useEffect(() => {
    if (!isContentReady) return;

    if (cover) {
      void addCover();
    }
    if (disableAutoplay) {
      void handleVideos();
    }
  }, [isContentReady, containerId, cover, disableAutoplay]);

  return (
    <>
      {!isContentReady && <FlowboxSkeleton type={type} />}

      {containerId && <div id={containerId}></div>}
    </>
  );
};

export default Flowbox;
